<template>
  <div class="plot-map" :class="{ 'plot-map--fullscreen': isCssFullscreen }" ref="plotMapRef">
    <div class="map-controls">
      <div class="controls-left">
        <span v-if="geomType === 'POLYGON' && area > 0" class="area-display">
          <strong>{{ area.toFixed(0) }} m²</strong>
        </span>
        <span v-else-if="geomType === 'LINESTRING' && vertices.length > 0" class="area-display">
          <strong>{{ t('plotMap.pointsCount', { count: vertices.length }) }}</strong>
        </span>
      </div>
      <div class="controls-right">
        <AeButton
          v-if="vertices.length > 0 && geomType !== 'POINT'"
          :variant="editMode ? 'primary' : 'secondary'"
          small
          iconOnly
          @click="toggleEditMode"
        >
          <AeIcon :name="editMode ? 'check' : 'edit'" :size="16" />
          <span>{{ editMode ? t('plotMap.editActive') : t('plotMap.edit') }}</span>
        </AeButton>
        <AeButton
          v-if="vertices.length > 0 && geomType !== 'POINT'"
          variant="secondary"
          small
          iconOnly
          @click="undoLastVertex"
        >
          <AeIcon name="undo" :size="16" />
          <span>{{ t('plotMap.undo') }}</span>
        </AeButton>
        <AeButton
          v-if="vertices.length > 0"
          variant="danger"
          small
          iconOnly
          @click="clearShape"
        >
          <AeIcon name="trash" :size="16" />
          <span>{{ t('plotMap.delete') }}</span>
        </AeButton>
        <template v-if="vertices.length === 0">
          <small v-if="geomType === 'POLYGON'">{{ t('plotMap.clickToDrawPolygon') }}</small>
          <small v-else-if="geomType === 'LINESTRING'">{{ t('plotMap.clickToDrawLine') }}</small>
          <small v-else>{{ t('plotMap.clickToPlacePoint') }}</small>
        </template>
      </div>
    </div>

    <div ref="mapContainer" class="map-container"></div>

    <div v-if="!canDraw" class="map-overlay">
      <p>⚠️ {{ t('plotMap.enableDrawingHint') }}</p>
    </div>

    <div v-if="multiPolygonWarning === 'single'" class="mp-warning mp-warning--info">
      {{ t('plotMap.multiPolygonSingleWarning') }}
    </div>
    <div v-else-if="multiPolygonWarning === 'multi'" class="mp-warning mp-warning--warn">
      ⚠️ {{ t('plotMap.multiPolygonMultiWarning') }}
      <strong>{{ t('plotMap.multiPolygonDataLossWarning') }}</strong>
    </div>

    <div v-else class="editor-help">
      <!-- POLYGON -->
      <template v-if="geomType === 'POLYGON'">
        <p v-if="!editMode && vertices.length === 0">
          <strong>{{ t('plotMap.polygonStartTitle') }}</strong>. {{ t('plotMap.polygonStartHint') }}
        </p>
        <p v-else-if="editMode">
          <strong>{{ t('plotMap.editModeLabel') }}</strong> {{ t('plotMap.polygonEditHint') }}
        </p>
        <p v-else>
          <strong>{{ t('plotMap.polygonDoneTitle') }}</strong> {{ t('plotMap.polygonDoneHint') }}
        </p>
      </template>
      <!-- LINESTRING -->
      <template v-else-if="geomType === 'LINESTRING'">
        <p v-if="!editMode && vertices.length === 0">
          <strong>{{ t('plotMap.lineStartTitle') }}</strong>. {{ t('plotMap.lineStartHint') }}
        </p>
        <p v-else-if="editMode">
          <strong>{{ t('plotMap.editModeLabel') }}</strong> {{ t('plotMap.lineEditHint') }}
        </p>
        <p v-else>
          <strong>{{ t('plotMap.lineDoneTitle') }}</strong> {{ t('plotMap.lineDoneHint') }}
        </p>
      </template>
      <!-- POINT -->
      <template v-else>
        <p v-if="vertices.length === 0">
          <strong>{{ t('plotMap.pointStartTitle') }}</strong>.
        </p>
        <p v-else>
          <strong>{{ t('plotMap.pointDoneTitle') }}</strong> {{ t('plotMap.pointDoneHint') }}
        </p>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { AeButton, AeIcon } from '@aevue/components'

const { t } = useI18n()

const props = defineProps({
  centerLat: {
    type: Number,
    default: null
  },
  centerLon: {
    type: Number,
    default: null
  },
  geometry: {
    type: [Object, Array],
    default: null
  },
  otherPlots: {
    type: Array,
    default: () => []
  },
  height: {
    type: String,
    default: '400px'
  },
  geomType: {
    type: String,
    default: 'POLYGON',
    validator: (v) => ['POLYGON', 'LINESTRING', 'POINT'].includes(v)
  }
})

const emit = defineEmits(['update:geometry', 'update:area'])

const mapContainer = ref(null)
const plotMapRef   = ref(null)
const vertices     = ref([])
const area         = ref(0)
const editMode     = ref(false)
const isFullscreen = ref(false)
const isCssFullscreen = ref(false)
const multiPolygonWarning = ref(null)  // null | 'single' | 'multi'

let map           = null
let shape         = null  // L.polygon or L.polyline depending on geomType
let markers       = []
let isDrawing     = false
let otherPlotsLayer = null
let resizeObserver  = null
let fsBtn           = null
let ignoreNextMapClick = false

const FS_ICON  = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>`
const FSX_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>`

const canDraw = computed(() => props.centerLat != null && props.centerLon != null)

onMounted(() => {
  nextTick(() => {
    initMap()
    drawOtherPlots()
    if (props.geometry) {
      loadGeometry(props.geometry)
    }
  })
})

onUnmounted(() => {
  console.log('Unmounting PlotMap component, cleaning up map and observers')
  document.removeEventListener('fullscreenchange', onFullscreenChange)
  document.removeEventListener('webkitfullscreenchange', onFullscreenChange)
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (map) {
    map.remove()
    map = null
  }
  fsBtn = null
})

watch(() => props.geometry, (newGeometry) => {
  if (newGeometry && map) {
    loadGeometry(newGeometry)
  }
})

watch(() => props.otherPlots, () => {
  drawOtherPlots()
}, { deep: true })

function initMap() {
  if (!mapContainer.value) return

  delete L.Icon.Default.prototype._getIconUrl
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  })

  const center = canDraw.value ? [props.centerLat, props.centerLon] : [42.5, 12.5]
  const zoom   = canDraw.value ? 15 : 6

  map = L.map(mapContainer.value, {
    center,
    zoom,
    zoomControl: true,
    doubleClickZoom: false,
    keyboard: false
  })

  L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
    attribution: 'Tiles &copy; Esri',
    maxZoom: 19
  }).addTo(map)

  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap, &copy; CartoDB',
    maxZoom: 19,
    pane: 'shadowPane'
  }).addTo(map)

  if (canDraw.value) {
    L.marker([props.centerLat, props.centerLon], {
      icon: L.divIcon({
        html: '🏠',
        className: 'center-marker',
        iconSize: [24, 24]
      })
    }).addTo(map).bindTooltip(t('plotMap.centerMarkerTooltip'), { permanent: false })
  }

  map.on('click', handleMapClick)

  resizeObserver = new ResizeObserver(() => {
    if (map) map.invalidateSize()
  })
  resizeObserver.observe(mapContainer.value)

  nextTick(() => map.invalidateSize())

  const FullscreenControl = L.Control.extend({
    options: { position: 'topleft' },
    onAdd() {
      const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control')
      const btn = L.DomUtil.create('a', 'leaflet-control-fs', container)
      btn.href = '#'
      btn.title = t('plotMap.fullscreen')
      btn.innerHTML = FS_ICON
      L.DomEvent.disableClickPropagation(container)
      L.DomEvent.on(btn, 'click', L.DomEvent.preventDefault)
      L.DomEvent.on(btn, 'click', toggleFullscreen)
      fsBtn = btn
      return container
    }
  })
  new FullscreenControl().addTo(map)
  document.addEventListener('fullscreenchange', onFullscreenChange)
  document.addEventListener('webkitfullscreenchange', onFullscreenChange)
}

async function toggleFullscreen() {
  const el = plotMapRef.value
  const supportsNative = !!(el?.requestFullscreen || el?.webkitRequestFullscreen)

  if (!supportsNative) {
    // Fallback CSS per iOS WKWebView dove la Fullscreen API non è supportata
    ignoreNextMapClick = true
    setTimeout(() => { ignoreNextMapClick = false }, 400)
    isCssFullscreen.value = !isCssFullscreen.value
    isFullscreen.value = isCssFullscreen.value
    if (fsBtn) {
      fsBtn.innerHTML = isFullscreen.value ? FSX_ICON : FS_ICON
      fsBtn.title = isFullscreen.value ? t('plotMap.exitFullscreen') : t('plotMap.fullscreen')
    }
    nextTick(() => map?.invalidateSize())
    return
  }

  try {
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (el?.requestFullscreen) {
        await el.requestFullscreen()
      } else if (el?.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen()
      }
    } else {
      if (document.exitFullscreen) {
        await document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      }
    }
  } catch (e) {
    // requestFullscreen può fallire in alcuni contesti (dialog, iframe)
    console.warn('Fullscreen API error:', e)
  }
}

function onFullscreenChange() {
  isFullscreen.value = !!(document.fullscreenElement || document.webkitFullscreenElement)
  if (fsBtn) {
    fsBtn.innerHTML = isFullscreen.value ? FSX_ICON : FS_ICON
    fsBtn.title = isFullscreen.value ? t('plotMap.exitFullscreen') : t('plotMap.fullscreen')
  }
  nextTick(() => map?.invalidateSize())
}

function loadGeometry(geometry) {
  if (!map || !geometry) return

  let latlngs = []

  if (Array.isArray(geometry)) {
    latlngs = geometry
  } else if (geometry.type === 'Point') {
    latlngs = [[geometry.coordinates[1], geometry.coordinates[0]]]
  } else if (geometry.type === 'LineString') {
    latlngs = geometry.coordinates.map(coord => [coord[1], coord[0]])
  } else if (geometry.type === 'Polygon') {
    latlngs = geometry.coordinates[0].slice(0, -1).map(coord => [coord[1], coord[0]])
  } else if (geometry.type === 'MultiPolygon') {
    multiPolygonWarning.value = geometry.coordinates.length > 1 ? 'multi' : 'single'
    const outer = geometry.coordinates.reduce((best, poly) =>
      poly[0].length > best.length ? poly[0] : best, geometry.coordinates[0][0])
    latlngs = outer.slice(0, -1).map(coord => [coord[1], coord[0]])
  } else if (geometry.geometry?.type === 'Point') {
    latlngs = [[geometry.geometry.coordinates[1], geometry.geometry.coordinates[0]]]
  } else if (geometry.geometry?.type === 'LineString') {
    latlngs = geometry.geometry.coordinates.map(coord => [coord[1], coord[0]])
  } else if (geometry.geometry?.type === 'Polygon') {
    latlngs = geometry.geometry.coordinates[0].slice(0, -1).map(coord => [coord[1], coord[0]])
  } else if (geometry.geometry?.type === 'MultiPolygon') {
    multiPolygonWarning.value = geometry.geometry.coordinates.length > 1 ? 'multi' : 'single'
    const outer = geometry.geometry.coordinates.reduce((best, poly) =>
      poly[0].length > best.length ? poly[0] : best, geometry.geometry.coordinates[0][0])
    latlngs = outer.slice(0, -1).map(coord => [coord[1], coord[0]])
  }
  
  if (latlngs.length > 0) {
    vertices.value = latlngs
    isDrawing = true
    if (props.geomType !== 'POINT') editMode.value = true
    calculateArea()
    renderShape()

    nextTick(() => {
      if (shape) {
        if (shape.getBounds) map.fitBounds(shape.getBounds(), { padding: [50, 50] })
      } else if (vertices.value.length > 0) {
        map.setView(vertices.value[0], 15)
      }
    })
  }
}

function handleMapClick(e) {
  if (ignoreNextMapClick) return
  if (editMode.value) return
  if (!canDraw.value) return

  const { lat, lng } = e.latlng

  // POINT: always replace the single vertex
  if (props.geomType === 'POINT') {
    vertices.value = [[lat, lng]]
    isDrawing = true
    calculateAndEmit()
    renderShape()
    return
  }

  // POLYGON: emit on each additional point once closed
  if (props.geomType === 'POLYGON' && vertices.value.length >= 3) {
    calculateAndEmit()
  }

  vertices.value.push([lat, lng])
  isDrawing = true

  const minPoints = props.geomType === 'LINESTRING' ? 2 : 3
  if (vertices.value.length >= minPoints) {
    calculateAndEmit()
  }

  renderShape()
}

function renderShape() {
  clearLayers()
  if (vertices.value.length === 0) return

  // Draw the shape
  if (props.geomType === 'POLYGON' && vertices.value.length >= 3) {
    shape = L.polygon(vertices.value, {
      color: '#667eea',
      fillColor: '#667eea',
      fillOpacity: 0.3,
      weight: 3
    }).addTo(map)
  } else if (props.geomType === 'LINESTRING' && vertices.value.length >= 2) {
    shape = L.polyline(vertices.value, {
      color: '#667eea',
      weight: 3
    }).addTo(map)
  }
  // POINT: only a marker below, no shape

  // Show vertex markers:
  // - POINT: always (marker is the element itself)
  // - POLYGON/LINESTRING: always while drawing or in edit mode
  const showMarkers = props.geomType === 'POINT' || editMode.value || isDrawing

  if (showMarkers) {
    vertices.value.forEach((vertex, index) => {
      const isDraggable = editMode.value || props.geomType === 'POINT'
      const marker = L.marker(vertex, {
        draggable: isDraggable,
        icon: L.divIcon({
          className: 'vertex-marker',
          html: `<div class="vertex-dot ${isDraggable ? 'editable' : ''}">${props.geomType === 'POINT' ? '📍' : index + 1}</div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        })
      }).addTo(map)

      if (isDraggable) {
        marker.on('drag', (e) => handleVertexDrag(index, e))
        marker.on('dragend', () => {
          calculateAndEmit()
          renderShape()
        })

        // Click to delete vertex (not for POINT, min 3 for POLYGON, min 2 for LINESTRING)
        if (editMode.value && props.geomType !== 'POINT') {
          const minKeep = props.geomType === 'LINESTRING' ? 2 : 3
          marker.on('click', (e) => {
            L.DomEvent.stopPropagation(e)
            if (vertices.value.length > minKeep) {
              deleteVertex(index)
            }
          })
        }
      }

      markers.push(marker)
    })

    // Midpoint handlers in edit mode
    if (editMode.value) {
      if (props.geomType === 'POLYGON' && vertices.value.length >= 3) {
        addMidpointHandlers(true)  // wrap-around
      } else if (props.geomType === 'LINESTRING' && vertices.value.length >= 2) {
        addMidpointHandlers(false) // no wrap-around
      }
    }
  }
}

function addMidpointHandlers(wrapAround) {
  const len = vertices.value.length
  const count = wrapAround ? len : len - 1

  for (let i = 0; i < count; i++) {
    const v1 = vertices.value[i]
    const v2 = vertices.value[(i + 1) % len]

    const midLat = (v1[0] + v2[0]) / 2
    const midLng = (v1[1] + v2[1]) / 2

    const midMarker = L.marker([midLat, midLng], {
      draggable: true,
      icon: L.divIcon({
        className: 'midpoint-marker',
        html: '<div class="midpoint-dot">+</div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      })
    }).addTo(map)

    const insertIndex = i + 1
    let inserted = false

    midMarker.on('dragstart', () => { inserted = false })

    midMarker.on('drag', (e) => {
      const { lat, lng } = e.latlng
      if (!inserted) {
        vertices.value.splice(insertIndex, 0, [lat, lng])
        inserted = true
      } else {
        vertices.value[insertIndex] = [lat, lng]
      }
      if (shape) shape.setLatLngs(vertices.value)
    })

    midMarker.on('dragend', () => {
      if (inserted) {
        calculateAndEmit()
        renderShape()
      }
    })

    markers.push(midMarker)
  }
}

function deleteVertex(index) {
  vertices.value.splice(index, 1)
  calculateAndEmit()
  renderShape()
}

function handleVertexDrag(index, e) {
  const { lat, lng } = e.latlng
  vertices.value[index] = [lat, lng]
  if (shape) shape.setLatLngs(vertices.value)
}

function toggleEditMode() {
  editMode.value = !editMode.value
  renderShape()
}

function undoLastVertex() {
  if (vertices.value.length === 0) return

  vertices.value.pop()

  const minPoints = props.geomType === 'LINESTRING' ? 2 : 3
  if (vertices.value.length >= minPoints) {
    calculateAndEmit()
  } else {
    area.value = 0
    emit('update:geometry', null)
    emit('update:area', 0)
  }
  renderShape()
}

function clearShape() {
  if (!confirm(t('plotMap.confirmDeleteGeometry'))) return

  vertices.value = []
  area.value = 0
  isDrawing = false
  editMode.value = false
  clearLayers()

  emit('update:geometry', null)
  emit('update:area', 0)
}

function clearLayers() {
  if (shape) {
    map.removeLayer(shape)
    shape = null
  }
  markers.forEach(marker => map.removeLayer(marker))
  markers = []
}

function calculateArea() {
  if (props.geomType !== 'POLYGON' || vertices.value.length < 3) {
    area.value = 0
    return
  }
  area.value = parseFloat(calculateGeodesicArea(vertices.value).toFixed(0))
}

function calculateAndEmit() {
  if (props.geomType === 'POLYGON') {
    if (vertices.value.length < 3) return
    calculateArea()
    const coordinates = vertices.value.map(v => [v[1], v[0]])
    coordinates.push(coordinates[0])
    emit('update:geometry', {
      coordinates: vertices.value,
      geojson: { type: 'Polygon', coordinates: [coordinates] }
    })
    emit('update:area', area.value)

  } else if (props.geomType === 'LINESTRING') {
    if (vertices.value.length < 2) return
    emit('update:geometry', {
      coordinates: vertices.value,
      geojson: { type: 'LineString', coordinates: vertices.value.map(v => [v[1], v[0]]) }
    })
    emit('update:area', 0)

  } else if (props.geomType === 'POINT') {
    if (vertices.value.length < 1) return
    const v = vertices.value[0]
    emit('update:geometry', {
      coordinates: vertices.value,
      geojson: { type: 'Point', coordinates: [v[1], v[0]] }
    })
    emit('update:area', 0)
  }
}

function calculateGeodesicArea(latlngs) {
  const EARTH_RADIUS = 6378137
  if (latlngs.length < 3) return 0
  let totalArea = 0
  const len = latlngs.length
  for (let i = 0; i < len; i++) {
    const p1 = latlngs[i]
    const p2 = latlngs[(i + 1) % len]
    const lat1 = p1[0] * Math.PI / 180
    const lat2 = p2[0] * Math.PI / 180
    const lon1 = p1[1] * Math.PI / 180
    const lon2 = p2[1] * Math.PI / 180
    totalArea += (lon2 - lon1) * (2 + Math.sin(lat1) + Math.sin(lat2))
  }
  return Math.abs(totalArea * EARTH_RADIUS * EARTH_RADIUS / 2)
}

function drawOtherPlots() {
  if (!map) return

  if (otherPlotsLayer) {
    map.removeLayer(otherPlotsLayer)
    otherPlotsLayer = null
  }

  if (!props.otherPlots || props.otherPlots.length === 0) return

  otherPlotsLayer = L.featureGroup()

  props.otherPlots.forEach(plot => {
    if (!plot.geometry && !plot.geojson) return

    let latlngs = []

    if (plot.geometry?.type === 'Polygon') {
      latlngs = plot.geometry.coordinates[0].map(coord => [coord[1], coord[0]])
    } else if (plot.geojson) {
      const geom = typeof plot.geojson === 'string' ? JSON.parse(plot.geojson) : plot.geojson
      if (geom.type === 'Polygon') {
        latlngs = geom.coordinates[0].map(coord => [coord[1], coord[0]])
      } else if (geom.geometry?.type === 'Polygon') {
        latlngs = geom.geometry.coordinates[0].map(coord => [coord[1], coord[0]])
      }
    }

    if (latlngs.length > 0) {
      L.polygon(latlngs, {
        color: '#000',
        fillColor: '#ccc',
        fillOpacity: 0.4,
        weight: 2
      }).addTo(otherPlotsLayer).bindTooltip(plot.name || t('plotMap.otherPlotFallback'), { sticky: true })
    }
  })

  otherPlotsLayer.addTo(map)
}
</script>

<style scoped>
.plot-map {
  position: relative;
  width: 100%;
}

.map-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  background: #f9f9f9;
  border-radius: 6px;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.controls-left {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-shrink: 0;
}

.controls-right {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.area-display {
  font-size: 0.9rem;
  color: #666;
  white-space: nowrap;
}

.area-display strong {
  color: #667eea;
  font-size: 1rem;
}

.map-container {
  width: 100%;
  height: v-bind(height);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  isolation: isolate;
}

.mp-warning {
  padding: 0.45rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  line-height: 1.5;
}
.mp-warning--info {
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.25);
  color: #1d4ed8;
}
.mp-warning--warn {
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.4);
  color: #92400e;
}

.map-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 8px;
  margin-top: calc(48px + 0.75rem);
}

.map-overlay p {
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  color: #666;
  font-size: 0.875rem;
}

.editor-help {
  padding: 0.75rem;
  background: #e3f2fd;
  border-radius: 6px;
  border-left: 4px solid #2196f3;
}

.editor-help p {
  margin: 0;
  font-size: 0.9rem;
  color: #1976d2;
  line-height: 1.5;
}

:deep(.center-marker) {
  background: none;
  border: none;
  font-size: 24px;
  text-align: center;
  line-height: 24px;
}

:deep(.leaflet-control-zoom) {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

:deep(.leaflet-control-zoom a) {
  width: 32px;
  height: 32px;
  line-height: 32px;
  font-size: 18px;
}

:deep(.vertex-marker) {
  background: none;
  border: none;
}

:deep(.vertex-dot) {
  width: 24px;
  height: 24px;
  background: white;
  border: 3px solid #667eea;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
  color: #667eea;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

:deep(.vertex-dot.editable) {
  cursor: move;
  border-color: #3498db;
  color: #3498db;
}

:deep(.vertex-dot.editable:hover) {
  transform: scale(1.2);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

:deep(.midpoint-marker) {
  background: none;
  border: none;
  cursor: pointer;
}

:deep(.midpoint-dot) {
  width: 16px;
  height: 16px;
  background: white;
  border: 2px solid #3498db;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: bold;
  color: #3498db;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  opacity: 0.7;
  transition: all 0.2s;
}

:deep(.midpoint-dot:hover) {
  opacity: 1;
  transform: scale(1.3);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
}

/* ─── Fullscreen ──────────────────────────────────────────────────────────── */
/* CSS fallback per iOS WKWebView (Capacitor) dove la Fullscreen API non è supportata */
.plot-map--fullscreen {
  position: fixed !important;
  inset: 0 !important;
  z-index: 9999 !important;
  display: flex;
  flex-direction: column;
  background: #111;
  padding: 0;
}

.plot-map--fullscreen .map-controls {
  border-radius: 0;
  flex-shrink: 0;
}

.plot-map--fullscreen .map-container {
  flex: 1;
  height: unset !important;
  border-radius: 0;
}

.plot-map--fullscreen .editor-help {
  border-radius: 0;
  flex-shrink: 0;
}

.plot-map:fullscreen {
  display: flex;
  flex-direction: column;
  background: #111;
  padding: 0;
}

.plot-map:fullscreen .map-controls {
  border-radius: 0;
  flex-shrink: 0;
}

.plot-map:fullscreen .map-container {
  flex: 1;
  height: unset !important;
  border-radius: 0;
}

.plot-map:fullscreen .editor-help {
  border-radius: 0;
  flex-shrink: 0;
}

:deep(.leaflet-control-fs) {
  width: 32px;
  height: 32px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  background: white;
  cursor: pointer;
  text-decoration: none;
  color: #333;
}

:deep(.leaflet-control-fs:hover) {
  background: #f4f4f4;
}

:deep(.leaflet-control-fs svg) {
  fill: #333;
  display: block;
}
</style>
